<template>
  <div class="ArtistPortfolio mt-10">
    <div
      id="ArtistFilterNav"
      class="FilterNav overflow-x-auto md:overflow-x-visible pt-2 sm:pt-4 lg:pt-6 border-b border-t border-grey-dark w-full flex justify-between md:justify-start"
    >
      <div class="menu-item mr-4 md:mr-10">
        <a
          :href="`${artist.slug}`"
          :class="tabIsClass('available')"
          @click.prevent="setTab('available')"
          class="whitespace-nowrap pt-4 pb-4 sm:pb-6 lg:pb-8"
        >
          <MqLayout mq="md+"> <span>Artwork</span> ({{ artist.counts.available }}) </MqLayout>
          <MqLayout :mq="['xs', 'sm']"> <span>Art</span> ({{ artist.counts.available }}) </MqLayout>
        </a>
      </div>
      <div class="menu-item mr-4 md:mr-10" v-if="artist.counts.reprints > 0">
        <a
          :href="`${artist.slug}/reprints`"
          :class="tabIsClass('reprints')"
          @click.prevent="setTab('reprints')"
          class="whitespace-nowrap pt-4 pb-4 sm:pb-6 lg:pb-8"
        >
          Prints ({{ artist.counts.reprints }})
        </a>
      </div>
      <div class="menu-item mr-4 md:mr-10" v-if="artist.counts.sold > 0">
        <a
          :href="`${artist.slug}/sold`"
          :class="tabIsClass('sold')"
          @click.prevent="setTab('sold')"
          class="whitespace-nowrap pt-4 pb-4 sm:pb-6 lg:pb-8"
        >
          Sold ({{ artist.counts.sold }})
        </a>
      </div>
      <div class="menu-item mr-4 md:mr-10" v-if="artist.paid_plan && artist.counts.series > 0">
        <a :href="`/${artist.slug}/series`" class="whitespace-nowrap pt-4 pb-4 sm:pb-6 lg:pb-8">
          Series ({{ artist.counts.series }})
        </a>
      </div>
      <div class="menu-item mr-4 md:mr-10" v-if="artist.paid_plan && studioPostsData?.data?.posts_count > 0">
        <a
          :href="`${artist.slug}/studio`"
          :class="tabIsClass('studio')"
          @click.prevent="setTab('studio')"
          class="whitespace-nowrap pt-4 pb-4 sm:pb-6 lg:pb-8"
        >
          <span v-if="['md', 'lg'].includes($mq)">In The Studio</span>
          <span v-else>In Studio</span>
        </a>
      </div>
      <div class="menu-item">
        <a
          :href="`${artist.slug}/bio`"
          :class="tabIsClass('bio')"
          @click.prevent="setTab('bio')"
          class="whitespace-nowrap pt-4 pb-4 sm:pb-6 lg:pb-8"
        >
          Artist Biography
        </a>
      </div>
      <div class="ml-auto mr-4 hidden md:flex">
        <ArtistSort @updateSort="updateSort" @updatePer="updatePer" v-show="availableOrSoldOrReprints" />
      </div>
    </div>

    <div class="mt-8 w-full md:hidden">
      <ArtistSort @updateSort="updateSort" @updatePer="updatePer" v-show="availableOrSoldOrReprints" />
    </div>

    <div class="w-full">
      <Listings v-show="availableOrSoldOrReprints" />
      <ArtistStudio v-if="tabIs('studio')" />
      <ArtistAbout v-if="tabIs('bio')" />
    </div>
  </div>
</template>

<script setup>
import Listings from "./Listings.vue"
import ArtistSort from "./ArtistSort.vue"
import ArtistStudio from "./ArtistStudio.vue"
import ArtistAbout from "./ArtistAbout.vue"

import { useQuery } from "@tanstack/vue-query"
import { computed, provide, inject, ref } from "vue"
import Http from "@/lib/Http"

const artist = inject("artist")
const activeTab = inject("activeTab")
const setTab = inject("setTab")
const page = inject("page")
const sort = inject("sort")

const per = ref(20)
const scope = computed(() => {
  if (activeTab.value === "sold") return "sold"
  if (activeTab.value === "reprints") return "reprints"
  return "available"
})

provide("sort", sort)
provide("per", per)
provide("scope", scope)
provide("page", page)
provide("setPage", (newValue) => (page.value = newValue))

const tabIs = (name) => {
  return activeTab.value === name
}
const tabIsClass = (selected) => {
  const isActive = tabIs(selected)
  return { "border-primary border-b-[5px]": isActive, "border-b-[5px] border-transparent": !isActive }
}

const availableOrSoldOrReprints = computed(() => {
  return tabIs("available") || tabIs("sold") || tabIs("reprints")
})

const updateSort = (newValue) => {
  sort.value = newValue
}

const updatePer = (newValue) => {
  per.value = newValue
}

const { data: studioPostsData } = useQuery({
  queryKey: ["studioPosts"],
  queryFn: async () => await Http.get(`/api/artists/${artist.id}/studio_posts`),
  refetchOnWindowFocus: false,
})
</script>

<script>
export default {
  name: "ArtistPortfolio",
}
</script>

<style scoped>
.menu-item {
  padding: 0;
}

.menu-item:first-child,
.menu-item:last-child {
  margin-left: 0;
}

.ArtistPortfolio {
  min-height: 500px;
}

.ArtistPortfolio .FilterNav a {
  font-family: Lato;
  font-size: 16px;
  line-height: 19px;
  text-decoration: none;
  display: block;
}

@media screen and (max-width: theme("screens.md") - 1px) {
  .ArtistPortfolio .FilterNav a {
    font-size: 14px;
    line-height: 16px;
  }
}

@media (max-width: 320px) {
  .ArtistPortfolio .FilterNav a {
    font-size: 11px;
    line-height: 14px;
  }
}

.ArtistPortfolio .FilterNav a.active {
  border-bottom: 5px solid #089fd6;
}

.ArtistPortfolio .FilterNav a:active {
  text-decoration: none;
}
</style>
